/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect } from 'react';
import { Accept, useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';

import './styles.css';
import { CloseIcon } from '../icons';
import { payloadValidator } from '../../common/helper';
import toast from 'react-hot-toast';

const thumbsContainer: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
};

const thumbInner: React.CSSProperties = {
  display: 'flex',
  minWidth: 0,
  overflow: 'hidden',
};

const img: React.CSSProperties = {
  display: 'block',
  width: '100%',
  height: '100%',
  borderRadius: '5px',
};

const accept = [
  '.pdf',
  '.doc',
  '.docx',
  '.xls',
  '.xlsx',
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/bmp',
  'image/webp',
  'image/svg+xml',
].join(', ');

const isFile = (url: string): boolean => {
  const extension = url?.split('.')?.pop()?.toLowerCase();
  if (!extension) return false;

  const fileTypeMap: Record<string, string> = {
    pdf: 'pdf',
    doc: 'doc',
    docx: 'doc',
    xls: 'xls',
    xlsx: 'xlsx',
  };

  return fileTypeMap[extension] ||
    Object.values(fileTypeMap).some((fileType) => extension.includes(fileType))
    ? true
    : false;
};

interface FileInputProps {
  name: string;
  className?: string;
  files: any;
  handleFiles: (files: any, name?: string) => void;
  handleDownload?: () => void;
  placeholder?: React.ReactNode;
  fileClassName?: string;
}

export function FileInput({
  name,
  className,
  files,
  handleFiles,
  handleDownload,
  placeholder,
  fileClassName,
}: FileInputProps): JSX.Element {
  const { t } = useTranslation();
  const { getRootProps, getInputProps } = useDropzone({
    accept: accept as unknown as Accept,
    onDrop: (acceptedFiles) => {
      handleFileUpload(
        Object.assign(acceptedFiles[0], {
          preview: URL.createObjectURL(acceptedFiles[0]),
        }),
        name,
      );
    },
  });

  const handleFileUpload = (file: any, name: string) => {
    const validate: any = payloadValidator(file);
    if (validate.isValid) handleFiles(file, name);
    else return toast.error(validate?.message);
  }

  const thumbs = (
    <div className={`thumbs ${fileClassName}`}>
      <div style={thumbInner} className="items-center">
        {files ? (
          typeof files === 'string' ? (
            isFile(files) ? (
              <img
                style={{
                  width: 100,
                  height: 75,
                }}
                src="/file.png"
              />
            ) : (
              <img
                src={files}
                style={img}
                alt={name}
                className="object-contain"
              />
            )
          ) : files.type.startsWith('image/') ? (
            <img
              src={files.preview}
              style={img}
              alt={name}
              className="object-contain"
              onLoad={() => {
                URL.revokeObjectURL(files?.preview);
              }}
            />
          ) : (
            <img
              src="/file.png"
              style={{
                width: 100,
                height: 75,
              }}
              className="object-contain"
            />
          )
        ) : (
          <img
            src="/file.png"
            style={img}
            alt={name}
            className="object-contain"
          />
        )}
      </div>
    </div>
  );

  useEffect(() => {
    return () => URL.revokeObjectURL(files?.preview);
  }, []);

  return (
    <>
      <div style={{
        display: 'flex',
        flexDirection: 'row',
      }}>
        <div
          {...getRootProps({
            className: `dropzone file-input-container ${className}`,
          })}
        >
          <input {...getInputProps()} />
          <>
            {!files ? (
              placeholder ?? <p className="placeholder">{t('Attach File')}</p>
            ) : (
              <>
                {handleDownload ? (
                  <div className="download-btn">
                    <i
                      className="las la-download"
                      style={{
                        color: 'red',
                        cursor: 'pointer',
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownload();
                      }}
                    ></i>
                  </div>
                ) : null}
                {handleFiles ? (
                  <CloseIcon
                    onClick={(e) => {
                      e.stopPropagation();
                      handleFiles(null);
                    }}
                    className="remove-btn"
                    styles={{
                      color: 'red',
                      cursor: 'pointer',
                    }}
                  />
                ) : null}
                <aside style={thumbsContainer} className="thumbs-container">
                  {thumbs}
                </aside>
              </>
            )}
          </>
        </div>
        <div>
          {files?.name && files?.size && (
            <>
              <p className="file-info">
                <span className="file-name mx-1">FileName:</span>{files.name}
              </p>
              <p className="file-info">
                <span className="file-name mx-1">FileSize:</span>{(files.size / (1024 * 1024)).toFixed(2) + "MB"}
              </p>
            </>)}
        </div>
      </div>
    </>
  );
}
